import { FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  ChangeDetectorRef,
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  InjectionToken,
  Injector,
  OnDestroy,
  input,
  inject,
} from '@angular/core';
import { CopyBalloonButtonComponent } from './copy-balloon-button.component';

@Directive({
  selector: '[payCopyable]',
  standalone: true,
})
export class copyBalloonButtonDirective implements AfterViewInit, OnDestroy {
  protected el = inject(ElementRef);
  private overlay = inject(Overlay);
  private injector = inject(Injector);
  private _cd = inject(ChangeDetectorRef);

  @HostListener('mouseenter', ['$event'])
  onMouseEnter(event: MouseEvent) {
    this.makeOverlay();
    this.componentRef.instance.makeVisible();
    event.stopPropagation();
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    event.stopPropagation();
    this.componentRef.instance.copyValueToClipboard(event);
  }

  @HostListener('mouseleave', ['$event'])
  onMouseLeave(event: MouseEvent) {
    this.componentRef.instance.makeHidden();
    event.stopPropagation();
  }

  readonly textToCopy = input.required<string | undefined>();
  readonly copyEnabled = input(true);

  interval?: number;
  userProfilePortal!: CopyBalloonButtonComponent;
  componentRef!: ComponentRef<CopyBalloonButtonComponent>;
  overlayRef!: OverlayRef;

  ngOnDestroy(): void {
    if (this.componentRef) {
      this.componentRef.instance.makeHidden();
    }
  }

  ngAfterViewInit() {
    if (this.copyEnabled()) {
      this.el.nativeElement.classList.add('cursor-pointer', 'select-none');
    }
  }

  makeOverlay() {
    if (!this.copyEnabled()) {
      return;
    }

    const positionStrategy: FlexibleConnectedPositionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.el.nativeElement)
      .withGrowAfterOpen()
      .withPositions([
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
        },
      ]);

    if (this.overlayRef) {
      this.breakOverlay();
    }

    const injector = Injector.create({
      parent: this.injector,
      providers: [
        {
          provide: COPYVALUE,
          useValue: this.textToCopy(),
        },
      ],
    });

    this.overlayRef = this.overlay.create({
      positionStrategy: positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'balloon-overlay',
    });
    this.userProfilePortal = new ComponentPortal(CopyBalloonButtonComponent, null, injector) as unknown as CopyBalloonButtonComponent;

    this.componentRef = this.overlayRef.attach(this.userProfilePortal);

    this.overlayRef.updatePosition();

    this.interval = setInterval(() => {
      this.overlayRef.updatePosition();
      if (this.componentRef.instance.hidden) {
        this.breakOverlay();
      }

      this._cd.markForCheck();
    }, 300) as unknown as number;

    this.componentRef.instance.destroy.subscribe(() => {
      this.breakOverlay();
    });
  }

  breakOverlay() {
    this.overlayRef.detach();
    this.overlayRef.dispose();
    clearInterval(this.interval);
  }
}

export const COPYVALUE = new InjectionToken<object>('COPYVALUE');
